/**
 * Что на самом деле отдаёт микрофон — до всякой нашей обработки.
 *
 * Появилось из вопроса, на который нечем было ответить: звук не понравился и
 * с шумодавом, и без него. Значит подозревать надо не модель, а источник, а
 * источник у браузера скрытный — устройство может отдавать 16 кГц вместо
 * сорока восьми, сводить в моно, держать своё подавление включённым вопреки
 * просьбе. Всё это видно в `getSettings`, и всё это меняет звук сильнее, чем
 * любая наша ручка.
 */

import { Track, type RemoteAudioTrack } from "livekit-client";
import { captureConstraints } from "./audio";
import { heldRoom } from "./room";

export interface MicReport {
  /** Имя устройства так, как его назвал браузер. */
  device: string;
  /** Частота источника. Не 48000 — половина разговора о качестве закончена. */
  rate: number | null;
  channels: number | null;
  /** Что попросили. */
  asked: Record<string, unknown>;
  /** Что из этого браузер в самом деле включил. */
  got: Record<string, unknown>;
}

/**
 * Своим потоком, а не дорожкой звонка.
 *
 * До дорожки звонка отсюда не дотянуться — комната живёт внутри `useCall`, —
 * а устройство и его ограничения от этого не меняются: открываем тем же
 * запросом и получаем то же самое.
 */
export async function micReport(): Promise<MicReport> {
  const asked = captureConstraints();
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { ...asked } });
  const track = stream.getAudioTracks()[0];

  try {
    const settings = track.getSettings() as MediaTrackSettings & { voiceIsolation?: boolean };

    return {
      device: track.label || "без имени",
      rate: settings.sampleRate ?? null,
      channels: settings.channelCount ?? null,
      asked: { ...asked },
      got: {
        echoCancellation: settings.echoCancellation,
        autoGainControl: settings.autoGainControl,
        noiseSuppression: settings.noiseSuppression,
        voiceIsolation: settings.voiceIsolation,
      },
    };
  } finally {
    // Микрофон отпускается сразу: индикатор записи, горящий после проверки,
    // пугает сильнее, чем помогает.
    for (const item of stream.getTracks()) item.stop();
  }
}

/**
 * Что происходит со звуком по дороге — уже в разговоре.
 *
 * Второе, чем звук портится помимо самого микрофона: сеть. Потерянный пакет
 * Opus заклеивает своей выдумкой (PLC), и звучит эта заплатка ровно так же,
 * как жалуются на шумодав, — металлически и с обрубленными окончаниями.
 * Отличить одно от другого на слух нельзя, зато видно в цифрах.
 *
 * Главное число здесь — `concealed`: доля отсчётов, которых по правде не
 * приезжало и которые декодер выдумал сам. Единицы процентов уже слышны.
 * Снимать надо на **слушающей** стороне: потери считает тот, кто их получил.
 */
export interface StreamReport {
  /** Чей это звук: наш микрофон или чужой голос. */
  who: string;
  codec: string | null;
  /** Частота Opus. 48000 — полнополосный, меньше — узкая полоса. */
  rate: number | null;
  channels: number | null;
  /** Килобиты в секунду, измеренные за окно, а не заказанные пресетом. */
  kbps: number | null;
  /** Доля потерянных пакетов, проценты. */
  lost: number | null;
  jitterMs: number | null;
  rttMs: number | null;
  /** Доля выдуманного декодером звука, проценты. Только на приёме. */
  concealed: number | null;
}

/** Сколько ждать между двумя снимками: скорость иначе не посчитать. */
const WINDOW = 3000;

export async function callReport(): Promise<StreamReport[]> {
  const room = heldRoom();
  if (!room) throw new Error("нет разговора: статистику снимать не с чего");

  const mic = room.localParticipant.getTrackPublication(Track.Source.Microphone)?.audioTrack;

  const voices: { who: string; read: () => Promise<RTCStatsReport | undefined> }[] = [];

  if (mic) voices.push({ who: "мы", read: () => mic.getRTCStatsReport() });

  for (const participant of room.remoteParticipants.values()) {
    const track = participant.getTrackPublication(Track.Source.Microphone)?.track as
      | RemoteAudioTrack
      | undefined;

    if (track) {
      voices.push({
        who: participant.identity || participant.sid,
        read: () => track.getRTCStatsReport(),
      });
    }
  }

  // Два снимка подряд: всё, что «в секунду», получается только разностью.
  const before = await Promise.all(voices.map((voice) => voice.read()));
  await new Promise((wait) => setTimeout(wait, WINDOW));
  const after = await Promise.all(voices.map((voice) => voice.read()));

  return voices.map((voice, index) => measure(voice.who, before[index], after[index]));
}


/**
 * Одна пара снимков в одну строку отчёта.
 *
 * Отправленный поток и принятый разбираются порознь, и это не удобство:
 * у отправленного своих потерь нет по определению — считает их тот, кто
 * получил, и приезжают они обратным отчётом `remote-inbound-rtp`. Заклейку
 * же знает только приёмная сторона, и у отправителя её нет вовсе.
 */
function measure(
  who: string,
  before: RTCStatsReport | undefined,
  after: RTCStatsReport | undefined,
): StreamReport {
  const blank: StreamReport = {
    who,
    codec: null,
    rate: null,
    channels: null,
    kbps: null,
    lost: null,
    jitterMs: null,
    rttMs: null,
    concealed: null,
  };

  const now = pick(after, "outbound-rtp") ?? pick(after, "inbound-rtp");
  if (!now || !after) return blank;

  const sending = now.type === "outbound-rtp";
  const then = pick(before, sending ? "outbound-rtp" : "inbound-rtp");

  // Потери и время оборота: у отправителя — из обратного отчёта, у
  // получателя — из его же собственного.
  const loss = sending ? pick(after, "remote-inbound-rtp") : now;
  const lossThen = sending ? pick(before, "remote-inbound-rtp") : then;

  const codec = after.get(String(now.codecId)) as
    | { mimeType?: string; clockRate?: number; channels?: number }
    | undefined;

  const bytes = grown(now, then, sending ? "bytesSent" : "bytesReceived");
  const packets = grown(now, then, sending ? "packetsSent" : "packetsReceived");
  const dropped = grown(loss, lossThen, "packetsLost");

  return {
    who,
    codec: codec?.mimeType ?? null,
    rate: codec?.clockRate ?? null,
    /*
     * По этому числу о моно и стерео судить нельзя, и оно тут скорее для
     * полноты. У Opus строка `rtpmap` всегда объявляет два канала независимо
     * от того, что идёт на самом деле, — а сюда попадает именно она. Замер
     * на заведомо моновом микрофоне показывает двойку.
     *
     * Настоящий ответ лежит в `fmtp`: есть там `stereo=1` или нет. Смотреть
     * его надо в `chrome://webrtc-internals` — из статистики он не виден.
     */
    channels: codec?.channels ?? null,
    kbps: round((bytes * 8) / (WINDOW / 1000) / 1000),
    lost: percent(dropped, dropped + packets),
    jitterMs: round(number(loss?.jitter) * 1000),
    rttMs: sending ? round(number(loss?.roundTripTime) * 1000) : null,
    concealed: sending
      ? null
      : percent(grown(now, then, "concealedSamples"), grown(now, then, "totalSamplesReceived")),
  };
}

/** Первая запись такого рода в отчёте. Звуковая дорожка тут одна. */
function pick(report: RTCStatsReport | undefined, type: string) {
  if (!report) return undefined;

  for (const entry of report.values()) {
    const record = entry as Record<string, unknown> & { type?: string };
    if (record.type === type) return record;
  }

  return undefined;
}

/**
 * Прирост за окно, а не всё с начала разговора.
 *
 * Разговор мог начаться час назад и первые полминуты рваться — среднее за
 * всё время об этом уже не скажет, а спрашивают всегда про сейчас.
 */
function grown(
  now: Record<string, unknown> | undefined,
  then: Record<string, unknown> | undefined,
  field: string,
): number {
  return number(now?.[field]) - number(then?.[field]);
}

function number(value: unknown): number {
  const asked = Number(value);
  return Number.isFinite(asked) ? asked : NaN;
}

/** Доля в процентах с одним знаком. `null` — делить было не на что. */
function percent(part: number, whole: number): number | null {
  return Number.isFinite(part) && Number.isFinite(whole) && whole > 0
    ? round((part / whole) * 100)
    : null;
}

function round(value: number): number | null {
  return Number.isFinite(value) ? Math.round(value * 10) / 10 : null;
}
